import { UseQueryResult } from "@tanstack/react-query";
import {
  FilterOutline24Icon,
  TimeOutline18Icon,
} from "@vygruppen/spor-icon-react";
import {
  trainIdentifierIsEqual,
  TrainIdentifierWithIncidentId,
  TrainIdentifierWithIncidentIdString,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import {
  AffectedTrain,
  AffectedTrainsResponse,
} from "features/CenterContent/RoleContent/Vaktleder/types";
import HandledTrainList from "features/VehicleSidebar/AffectedTrains/lists/HandledTrainList";
import { UnhandledTrainList } from "features/VehicleSidebar/AffectedTrains/lists/UnhandledTrainList";
import {
  AffectedTrainsContainer,
  StyledTabsContent,
  StyledTabsContents,
} from "features/VehicleSidebar/AffectedTrains/styles";
import { AffectedTrainFilter } from "features/VehicleSidebar/AffectedTrains/types";
import {
  getSessionStorageFilter,
  setSessionStorageFilter,
} from "features/VehicleSidebar/AffectedTrains/utils/filterAffectedTrains";
import {
  addPendingTrainsToState,
  batchUpdateSelectedTrainsState,
  calculateTrainsToShow,
  removePendingTrainsFromState,
  updateSelectedTrainsState,
} from "features/VehicleSidebar/AffectedTrains/utils/stateHandlers";
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { SectionButton } from "shared/components/buttons/SectionButton";
import { AlertsCounter } from "shared/components/dataDisplay/AlertsCounter";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { FailureMessageWithRetryButton } from "shared/components/feedback/FailureMessageWithRetryButton";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import {
  FilterChip,
  Flex,
  HStack,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
  VStack,
} from "shared/components/Spor";
import { useTheme } from "styled-components";

type AffectedTrainsContentProps = Pick<
  UseQueryResult<AffectedTrainsResponse>,
  "data" | "isPending" | "isSuccess" | "isError" | "refetch"
> & {
  renderModal?: (args: {
    modalOpen: boolean;
    setModalOpen: (open: boolean) => void;
    affectedTrainsData?: AffectedTrainsResponse;
    selectedTrains: AffectedTrain[];
    updateSelectedTrains: (train: AffectedTrain) => void;
    addPendingTrains: (trains: TrainIdentifierWithIncidentId[]) => void;
  }) => ReactNode;
};

export const AffectedTrainsContent: FC<AffectedTrainsContentProps> = ({
  data: affectedTrainsData,
  isPending,
  isSuccess,
  isError,
  refetch,
  renderModal,
}) => {
  const { t } = useTranslation();

  const [selectedTrains, setSelectedTrains] = useState<AffectedTrain[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [showUnhandledFilter, setShowUnhandledFilter] = useState<boolean>(
    () => getSessionStorageFilter() !== null,
  );
  const [showHandledFilter, setShowHandledFilter] = useState<boolean>(false);
  const [showNotAffectedFilter, setShowNotAffectedFilter] =
    useState<boolean>(false);
  const [unhandledTrainsFilter, setUnhandledTrainsFilter] =
    useState<AffectedTrainFilter>(getSessionStorageFilter);
  const [notAffectedTrainsFilter, setNotAffectedTrainsFilter] =
    useState<AffectedTrainFilter>(null);
  const [showPassedTrains, setShowPassedTrains] = useState<boolean>(false);
  const [tabIndex, setTabsIndex] = useState<number>(0);
  const [pendingTrains, setPendingTrains] = useState<
    TrainIdentifierWithIncidentIdString[]
  >([]);

  const theme = useTheme();

  const notAffectedTrains = useMemo(
    () =>
      affectedTrainsData?.affectedTrains.filter(
        (train) => train.state === "NOT_AFFECTED",
      ) || [],
    [affectedTrainsData?.affectedTrains],
  );

  const incidentsCount = useMemo(
    () =>
      new Set(
        affectedTrainsData?.affectedTrains.map((train) => train.incidentId) ??
          [],
      ).size,
    [affectedTrainsData?.affectedTrains],
  );

  const updateSelectedTrains = useCallback(
    (affectedTrain: AffectedTrain) =>
      updateSelectedTrainsState(affectedTrain, setSelectedTrains),
    [],
  );

  const batchUpdateSelectedTrains = useCallback(
    (at: AffectedTrain[]) =>
      batchUpdateSelectedTrainsState(at, setSelectedTrains),
    [],
  );

  const clearSelectedTrains = useCallback(() => setSelectedTrains([]), []);

  const addPendingTrains = useCallback(
    (trains: TrainIdentifierWithIncidentId[]) =>
      addPendingTrainsToState(trains, setPendingTrains),
    [],
  );

  const removePendingTrains = useCallback(
    (trains: TrainIdentifierWithIncidentId[]) =>
      removePendingTrainsFromState(trains, setPendingTrains),
    [],
  );

  const handleCreateEvents = useCallback(() => setModalOpen(true), []);

  const affectedTrainsToShow = useMemo(
    () =>
      calculateTrainsToShow(
        affectedTrainsData,
        selectedTrains,
        showPassedTrains,
      ),
    [affectedTrainsData, selectedTrains, showPassedTrains],
  );

  useEffect(() => {
    if (!affectedTrainsData?.affectedTrains) return;

    setSelectedTrains((prevSelectedTrains) =>
      prevSelectedTrains.filter((selectedTrain) =>
        affectedTrainsData.affectedTrains.some(
          ({ trainId, incidentId }) =>
            trainIdentifierIsEqual(trainId, selectedTrain.trainId) &&
            selectedTrain.incidentId === incidentId,
        ),
      ),
    );

    removePendingTrains(
      affectedTrainsData.affectedTrains.map(({ trainId, incidentId }) => ({
        ...trainId,
        incidentId,
      })),
    );
  }, [affectedTrainsData, removePendingTrains]);

  useEffect(() => {
    setShowPassedTrains(false);
    setSelectedTrains([]);
  }, [tabIndex]);

  useEffect(() => {
    setSessionStorageFilter(unhandledTrainsFilter);
  }, [unhandledTrainsFilter]);

  const tabs = ["unhandled", "handled", "notAffected"];

  const showFilterByTab: Record<number, boolean> = {
    0: showUnhandledFilter,
    1: showHandledFilter,
    2: showNotAffectedFilter,
  };
  const activeShowFilter = showFilterByTab[tabIndex] ?? false;

  const toggleActiveFilter = () => {
    if (tabIndex === 0) setShowUnhandledFilter((prev) => !prev);
    else if (tabIndex === 1) setShowHandledFilter((prev) => !prev);
    else setShowNotAffectedFilter((prev) => !prev);
  };

  const filterSeriesByTab: Record<number, number> = {
    0: unhandledTrainsFilter?.trainSeriesIds?.length ?? 0,
    2: notAffectedTrainsFilter?.trainSeriesIds?.length ?? 0,
  };
  const numberOfSelectedTrainSeries = filterSeriesByTab[tabIndex] ?? 0;

  const hasListFetchErrors =
    affectedTrainsData?.errorMessages.length !== undefined &&
    affectedTrainsData?.errorMessages.length > 0;
  const retryFailureMessage = t(
    "Noe gikk galt ved lasting av innhold. Last siden på nytt for å prøve igjen. Dersom feilen vedvarer, ta kontakt med IT.",
  );

  return (
    <Flex flexDir="column" overflowY="auto" width="100%" height="100%">
      <AffectedTrainsContainer>
        {isPending && <SkeletonLoader skeletonType="affectedTrains" />}
        {isError && <FailureMessage m="6px" />}
        {isSuccess && affectedTrainsData && (
          <>
            {hasListFetchErrors && (
              <ExpandableInfoMessage
                semantics="alarm"
                title={t("Kunne ikke hente berørte tog for enkelte hendelser")}
                style={{ margin: "12px" }}
              >
                {affectedTrainsData.errorMessages.map((stretch) => (
                  <Text key={stretch} m="6px">
                    {stretch}: {t("Kunne ikke hente berørte tog for hendelsen")}
                  </Text>
                ))}
              </ExpandableInfoMessage>
            )}
            <Tabs
              variant="accent"
              colorScheme="base"
              overflowY="auto"
              height="100%"
              onValueChange={(e: { value: string }) => {
                setTabsIndex(tabs.indexOf(e.value));
              }}
              defaultValue={tabs[0]}
              alignItems="flex-start"
              backgroundColor={theme.colorBackgroundMain}
              borderRadius={6}
              border={`1px solid ${theme.colorOutlineSecondary}`}
            >
              <VStack p="12px" width="100%">
                <HStack
                  justify="space-between"
                  flexWrap="wrap"
                  alignItems="center"
                  w="100%"
                >
                  <TabsList
                    background={theme.colorSuccessFill}
                    w="100%"
                    gap={0}
                  >
                    <TabsTrigger value={tabs[0]} flex="1">
                      <Flex gap="6px" alignItems="center">
                        <Text
                          variant="sm"
                          color={
                            tabIndex === 0
                              ? theme.colorTextInverted
                              : theme.colorTextSecondary
                          }
                        >
                          {t("Ubehandlet")}
                        </Text>
                        <AlertsCounter
                          alerts={
                            affectedTrainsData.affectedTrains.filter(
                              (train) =>
                                train.state === "AFFECTED" ||
                                train.state === "INDIRECTLY_AFFECTED",
                            ).length
                          }
                          semantics={
                            tabIndex === 0
                              ? "transparentInverted"
                              : "transparent"
                          }
                        />
                      </Flex>
                    </TabsTrigger>
                    <TabsTrigger value={tabs[1]} flex="1">
                      <Flex gap="6px" alignItems="center">
                        <Text
                          variant="sm"
                          color={
                            tabIndex === 1
                              ? theme.colorTextInverted
                              : theme.colorTextSecondary
                          }
                        >
                          {t("Behandlet")}
                        </Text>
                        <AlertsCounter
                          alerts={
                            affectedTrainsData.handledAffectedTrains.length
                          }
                          semantics={
                            tabIndex === 1
                              ? "transparentInverted"
                              : "transparent"
                          }
                        />
                      </Flex>
                    </TabsTrigger>
                    <TabsTrigger value={tabs[2]} flex="1">
                      <Flex gap="6px" alignItems="center">
                        <Text
                          variant="sm"
                          color={
                            tabIndex === 2
                              ? theme.colorTextInverted
                              : theme.colorTextSecondary
                          }
                        >
                          {t("Ikke berørt")}
                        </Text>
                        <AlertsCounter
                          alerts={notAffectedTrains.length}
                          semantics={
                            tabIndex === 2
                              ? "transparentInverted"
                              : "transparent"
                          }
                        />
                      </Flex>
                    </TabsTrigger>
                  </TabsList>
                </HStack>
                <Flex
                  width="100%"
                  justifyContent="space-between"
                  alignItems="center"
                  height="33px"
                  marginTop="3px"
                >
                  <Flex>
                    {tabIndex === 0 && (
                      <FilterChip
                        size="sm"
                        padding={1}
                        checked={showPassedTrains}
                        onCheckedChange={() =>
                          setShowPassedTrains((prev) => !prev)
                        }
                        icon={{
                          default: <TimeOutline18Icon />,
                          checked: <TimeOutline18Icon />,
                        }}
                      >
                        <Text variant="sm" textWrap="nowrap" fontWeight="400">
                          {t("Vis passerte tog")}
                        </Text>
                      </FilterChip>
                    )}
                  </Flex>
                  <Text variant="sm" whiteSpace="nowrap" mr="12px" ml="auto">
                    {t("{{count}} brudd", { count: incidentsCount })}
                  </Text>
                  <SectionButton
                    variant="tertiary"
                    checked={activeShowFilter}
                    onCheckedChange={toggleActiveFilter}
                    alerts={numberOfSelectedTrainSeries}
                    icon={<FilterOutline24Icon />}
                  />
                </Flex>
              </VStack>
              <Separator
                borderTop={`1px solid ${theme.colorOutlineSecondary}`}
              />
              <StyledTabsContents height="100%" w="100%">
                <StyledTabsContent value="unhandled">
                  {(() => {
                    if (affectedTrainsData.affectedTrains.length > 0) {
                      return (
                        <UnhandledTrainList
                          trains={affectedTrainsToShow}
                          selectedTrains={selectedTrains}
                          setTabsPulse={(_: number) => {}}
                          updateSelectedTrains={updateSelectedTrains}
                          clearSelectedTrains={clearSelectedTrains}
                          batchUpdatedSelectedTrains={batchUpdateSelectedTrains}
                          handleCreateEvents={handleCreateEvents}
                          showFilter={showUnhandledFilter}
                          filter={unhandledTrainsFilter}
                          setFilter={setUnhandledTrainsFilter}
                          pendingTrains={pendingTrains}
                          refetch={refetch}
                        />
                      );
                    }

                    if (affectedTrainsData.errorMessages.length > 0) {
                      return (
                        <FailureMessageWithRetryButton
                          customMessage={retryFailureMessage}
                          retryFunction={refetch}
                        />
                      );
                    }

                    return (
                      <NoDataMessage
                        height="100%"
                        pt="12px"
                        reason={t(
                          "Det er ingen ubehandlede tog for øyeblikket",
                        )}
                        border={`1px solid ${theme.colorOutlineSecondary}`}
                      />
                    );
                  })()}
                </StyledTabsContent>
                <StyledTabsContent value="handled">
                  {(() => {
                    if (affectedTrainsData.handledAffectedTrains.length > 0) {
                      return (
                        <HandledTrainList
                          trains={affectedTrainsData.handledAffectedTrains}
                          showFilter={showHandledFilter}
                        />
                      );
                    }

                    if (affectedTrainsData.errorMessages.length > 0) {
                      return (
                        <FailureMessageWithRetryButton
                          customMessage={retryFailureMessage}
                          retryFunction={refetch}
                        />
                      );
                    }

                    return (
                      <NoDataMessage
                        height="100%"
                        pt="12px"
                        reason={t("Det er ingen behandlede tog for øyeblikket")}
                        border={`1px solid ${theme.colorOutlineSecondary}`}
                      />
                    );
                  })()}
                </StyledTabsContent>
                <StyledTabsContent value="notAffected">
                  {(() => {
                    if (notAffectedTrains.length > 0) {
                      return (
                        <UnhandledTrainList
                          trains={notAffectedTrains}
                          selectedTrains={selectedTrains}
                          updateSelectedTrains={updateSelectedTrains}
                          clearSelectedTrains={clearSelectedTrains}
                          batchUpdatedSelectedTrains={batchUpdateSelectedTrains}
                          handleCreateEvents={handleCreateEvents}
                          showFilter={showNotAffectedFilter}
                          filter={notAffectedTrainsFilter}
                          setFilter={setNotAffectedTrainsFilter}
                          pendingTrains={pendingTrains}
                          refetch={refetch}
                          setTabsPulse={(_: number) => {}}
                          notAffected
                        />
                      );
                    }

                    if (affectedTrainsData.errorMessages.length > 0) {
                      return (
                        <FailureMessageWithRetryButton
                          customMessage={retryFailureMessage}
                          retryFunction={refetch}
                        />
                      );
                    }

                    return (
                      <NoDataMessage
                        height="100%"
                        pt="12px"
                        reason={t(
                          "Det er ingen ikke berørte tog for øyeblikket",
                        )}
                        border={`1px solid ${theme.colorOutlineSecondary}`}
                      />
                    );
                  })()}
                </StyledTabsContent>
              </StyledTabsContents>
            </Tabs>
          </>
        )}
      </AffectedTrainsContainer>
      {renderModal &&
        renderModal({
          modalOpen,
          setModalOpen,
          affectedTrainsData,
          selectedTrains,
          updateSelectedTrains,
          addPendingTrains,
        })}
    </Flex>
  );
};
